import React, { useState, useEffect } from "react";
import { Table, Button, Modal, Form, Select, Input, message, Tag, Mentions, Upload } from "antd";
import { PlusOutlined, DeleteOutlined, UploadOutlined, DownloadOutlined, EyeOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import {
    useGetApplicationsQuery,
    useAddApplicationMutation,
    useDeleteApplicationMutation,
    useUpdateApplicationMutation,
} from "@/redux/rtk/features/application/applicationApi";
import { useGetUniversitiesQuery } from "@/redux/rtk/features/university/universityApi";
import { useGetDegreesQuery } from "@/redux/rtk/features/degree/degreeApi";
import { useDispatch, useSelector } from "react-redux";
import { loadAllStaff } from "@/redux/rtk/features/hrm/user/userSlice";
import { addSingleAttachment } from "@/redux/rtk/features/CRM/attachment/attachmentSlice";
import { getAttachmentUrl } from "@/utils/functions";
import usePermissions from "@/utils/usePermissions";

const { Option } = Select;

export default function LeadApplications({ lead, onUpdate, isAgent = false }) {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [statusForm] = Form.useForm();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
    const [selectedUniversity, setSelectedUniversity] = useState(null);
    const [editingApp, setEditingApp] = useState(null);
    const [receiptFile, setReceiptFile] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const dispatch = useDispatch();

    const { list: staffList } = useSelector((state) => state.users);

    const { permissions, hasPermission } = usePermissions();
    const canCreate = hasPermission("create-application");
    const canReadAll = hasPermission("readAll-application");
    const canReadSingle = hasPermission("readSingle-application");
    const canUpdate = hasPermission("update-application");
    const canDelete = hasPermission("delete-application");

    const hasReadPermission = isAgent || canReadAll || canReadSingle;
    const canEditOrView = !isAgent && (canUpdate || canReadSingle);

    const { data: applications, isLoading: appsLoading, refetch: refetchApps } =
        useGetApplicationsQuery(lead.dealId ? { deal_id: lead.dealId } : (lead.id ? { lead_id: lead.id } : {}), {
            skip: !hasReadPermission
        });

    const { data: universitiesData, isLoading: uniLoading } = useGetUniversitiesQuery();
    const { data: degrees, isLoading: degreeLoading } = useGetDegreesQuery();

    const [addApplication, { isLoading: isAdding }] = useAddApplicationMutation();
    const [updateApplication, { isLoading: isUpdating }] = useUpdateApplicationMutation();
    const [deleteApplication] = useDeleteApplicationMutation();

    const statusOptions = [
        { value: "pending", label: t("application.statusOptions.pending"), color: "gold" },
        { value: "accepted", label: t("application.statusOptions.accepted"), color: "green" },
        { value: "rejected", label: t("application.statusOptions.rejected"), color: "red" },
        { value: "cancelled", label: t("application.statusOptions.cancelled"), color: "default" },
    ];

    const paymentStatusOptions = [
        { value: "pending", label: t("application.paymentStatusOptions.pending"), color: "gold" },
        { value: "paid", label: t("application.paymentStatusOptions.paid"), color: "green" },
        { value: "failed", label: t("application.paymentStatusOptions.failed"), color: "red" },
        { value: "refunded", label: t("application.paymentStatusOptions.refunded"), color: "default" },
        { value: "through", label: t("application.paymentStatusOptions.through"), color: "cyan" },
        { value: "normal", label: t("application.paymentStatusOptions.normal"), color: "blue" },
    ];

    const universities = Array.isArray(universitiesData) ? universitiesData : (universitiesData?.universities ?? universitiesData?.getAllUniversity ?? []);

    useEffect(() => {
        dispatch(loadAllStaff({ query: "all" }));
    }, [dispatch]);

    useEffect(() => {
        const appList = applications?.getAllApplication || [];
        if (isModalOpen && appList.length > 0) {
            const lastApp = appList[0]; // Assuming it's sorted by newest
            form.setFieldsValue({
                language: lastApp.language,
                university_id: lastApp.universityId,
                major_id: lastApp.majorId,
            });
            setSelectedUniversity(universities.find(u => u.id === lastApp.universityId));
        } else if (isModalOpen) {
            // Default values from lead if no applications
            form.setFieldsValue({
                language: lead.languageRequired,
            });
        }
    }, [isModalOpen, applications, lead, form, universities]);

    const handleOpenModal = () => {
        setIsModalOpen(true);
    };

    const handleOpenStatusModal = (record) => {
        setEditingApp(record);
        statusForm.setFieldsValue({
            status: record.status,
            note: record.note,
            replay: record.replay,
            university_code: record.universityCode,
            amount: record.amount,
            receipt: record.receipt,
            payment_status: record.paymentStatus || 'pending',
        });
        setIsStatusModalOpen(true);
    };

    const handleUniversityChange = (value) => {
        const uni = universities.find((u) => u.id === value);
        setSelectedUniversity(uni);
        form.setFieldsValue({ major_id: null });
    };

    const onFinish = async (values) => {
        try {
            const payload = {
                ...values,
                ...(lead.dealId ? { deal_id: lead.dealId } : (lead.id ? { lead_id: lead.id } : {})),
            };

            const resp = await addApplication(payload);
            if (resp.data) {
                setIsModalOpen(false);
                form.resetFields();
                if (onUpdate) onUpdate();
            }
        } catch (error) {
            console.error("Error adding application:", error);
        }
    };

    const onStatusFinish = async (values) => {
        try {
            setIsProcessing(true);
            // Upload receipt if file is selected
            if (receiptFile) {
                const uniName = editingApp.university?.universityName || "";
                const attachmentCustomName = `${t("application.paymentReceipt")}${uniName ? ` - ${uniName}` : ""}`;

                const formData = new FormData();
                formData.append('files[]', receiptFile);

                // Use correct keys and check for existence
                const leadId = editingApp.lead?.id || editingApp.leadId;
                const dealId = editingApp.deal?.id || editingApp.dealId;

                if (leadId) formData.append('leadId', leadId);
                if (dealId) formData.append('dealId', dealId);

                formData.append('attachmentType', 'receipt');
                formData.append('attachmentName', attachmentCustomName);

                const uploadResult = await dispatch(addSingleAttachment(formData)).unwrap();
                // successHandler returns { message: "success", data: { ... } }
                values.receipt = uploadResult.data?.attachmentPath || uploadResult.attachmentPath || receiptFile.name;
            }

            const resp = await updateApplication({
                id: editingApp.id,
                values: values,
            });
            if (resp.data) {
                message.success(t("application.successUpdate"));
                setIsStatusModalOpen(false);
                setEditingApp(null);
                setReceiptFile(null);
            }
        } catch (error) {
            console.error("Error updating application:", error);
            message.error(t("application.errorUpdate"));
        } finally {
            setIsProcessing(false);
        }
    };


    const handleDelete = (id) => {
        Modal.confirm({
            title: t("application.deleteConfirm"),
            okText: t("common.yes"),
            okType: 'danger',
            cancelText: t("common.no"),
            onOk: async () => {
                await deleteApplication({ id });
            },
        });
    };

    const columns = [
        {
            title: t("application.university"),
            dataIndex: ["university", "universityName"],
            key: "university",
            width: 150,
            ellipsis: true,
        },
        {
            title: t("application.major"),
            dataIndex: ["major", "name"],
            key: "major",
            width: 150,
            ellipsis: true,
        },
        {
            title: t("lead.requiredLanguage"),
            dataIndex: "language",
            key: "language",
            width: 100,
            render: (lang) => <Tag color="blue">{lang?.toUpperCase()}</Tag>,
        },
        {
            title: t("application.status"),
            dataIndex: "status",
            key: "status",
            width: 120,
            render: (status, record) => {
                const opt = statusOptions.find(o => o.value === status) || { label: status, color: "default" };
                return (
                    <Tag
                        className={canEditOrView ? "cursor-pointer" : ""}
                        color={opt.color}
                        onClick={() => canEditOrView && handleOpenStatusModal(record)}
                    >
                        {opt.label}
                    </Tag>
                );
            },
        },
        {
            title: t("common.note"),
            dataIndex: "note",
            key: "note",
            width: 200,
            render: (note, record) => (
                <div
                    className={`truncate max-w-[180px] ${canEditOrView ? "cursor-pointer" : ""} text-gray-500 italic`}
                    onClick={() => canEditOrView && handleOpenStatusModal(record)}
                    title={note}
                >
                    {note || t("common.noData")}
                </div>
            )
        },
        {
            title: t("application.amount"),
            dataIndex: "amount",
            key: "amount",
            render: (amount) => amount ? `$${parseFloat(amount).toFixed(2)}` : "-"
        },
        {
            title: t("application.paymentStatus"),
            dataIndex: "paymentStatus",
            key: "paymentStatus",
            render: (status, record) => {
                const opt = paymentStatusOptions.find(o => o.value === status) || { label: status || t("application.paymentStatusOptions.pending"), color: "orange" };
                return (
                    <Tag
                        className={canEditOrView ? "cursor-pointer" : ""}
                        color={opt.color}
                        onClick={() => canEditOrView && handleOpenStatusModal(record)}
                    >
                        {opt.label}
                    </Tag>
                );
            },
        },
        {
            title: t("application.receipt"),
            dataIndex: "receipt",
            key: "receipt",
            width: 100,
            render: (receipt) => receipt ? (
                <div className="flex items-center gap-2">
                    <Button
                        type="text"
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={(e) => {
                            e.stopPropagation();
                            window.open(getAttachmentUrl(receipt), "_blank");
                        }}
                        title={t("common.view")}
                        className="flex items-center justify-center p-0 h-auto text-blue-500"
                    />
                    <Button
                        type="text"
                        size="small"
                        icon={<DownloadOutlined />}
                        onClick={(e) => {
                            e.stopPropagation();
                            const url = getAttachmentUrl(receipt);
                            const link = document.createElement("a");
                            link.href = url;
                            link.target = "_blank";
                            link.download = "receipt";
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                        }}
                        title={t("attachment.download")}
                        className="flex items-center justify-center p-0 h-auto text-blue-500"
                    />
                </div>
            ) : "-"
        },
        {
            title: t("common.action"),
            key: "action",
            render: (_, record) => (
                <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => handleDelete(record.id)}
                />
            ),
        },
    ];

    if (!hasReadPermission) {
        return (
            <div className="p-4 text-center text-red-500 font-medium">
                {t("common.permissionError") || "You do not have permission to view applications."}
            </div>
        );
    }

    return (
        <div className="p-4">
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">{t("application.title")}</h3>
                {!isAgent && canCreate && (
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleOpenModal}>
                        {t("application.addApplication")}
                    </Button>
                )}
            </div>

            <Table
                dataSource={applications?.getAllApplication || []}
                columns={columns.filter(col => col.key !== 'action' || (!isAgent && canDelete))}
                loading={appsLoading}
                rowKey="id"
                pagination={false}
                scroll={{ x: 'max-content' }}
                onRow={(record) => ({
                    onClick: () => canEditOrView && handleOpenStatusModal(record),
                    className: canEditOrView ? 'cursor-pointer hover:bg-gray-50' : ''
                })}
            />

            {!isAgent && canCreate && (
                <div className="mt-6 flex justify-center">
                    <button
                        onClick={handleOpenModal}
                        className="flex items-center justify-center gap-2 w-full py-3 px-4 rounded-xl border-2 border-dashed border-primary/30 text-primary hover:border-primary hover:bg-primary/5 transition-all font-medium"
                    >
                        <PlusOutlined /> {t("application.addApplication")}
                    </button>
                </div>
            )}

            <Modal
                title={t("application.addNewApplication")}
                open={isModalOpen}
                onCancel={() => setIsModalOpen(false)}
                footer={null}>
                <Form form={form} layout="vertical" onFinish={onFinish}>
                    <Form.Item
                        name="university_id"
                        label={t("application.university")}
                        rules={[{ required: true, message: t("application.selectUniversity") }]}>
                        <Select
                            placeholder={t("application.selectUniversity")}
                            onChange={handleUniversityChange}
                            loading={uniLoading}
                            showSearch
                            filterOption={(input, option) =>
                                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                            }>
                            {universities.map((uni) => (
                                <Option key={uni.id} value={uni.id}>
                                    {uni.universityName}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="major_id"
                        label={t("application.major")}
                        rules={[{ required: true, message: t("application.selectMajor") }]}>
                        <Select
                            placeholder={t("application.selectMajor")}
                            disabled={!selectedUniversity}
                            showSearch
                            filterOption={(input, option) =>
                                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                            }>
                            {selectedUniversity?.majors?.map((major) => (
                                <Option key={major.id} value={major.id}>
                                    {major.name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="language"
                        label={t("application.studyLanguage")}
                        rules={[{ required: true, message: t("lead.selectLanguage") }]}>
                        <Select placeholder={t("lead.selectLanguage")}>
                            <Option value="en">{t("lead.languages.en")}</Option>
                            <Option value="tr">{t("lead.languages.tr")}</Option>
                            <Option value="ar">{t("lead.languages.ar")}</Option>
                            <Option value="fr">{t("lead.languages.fr")}</Option>
                            <Option value="ru">{t("lead.languages.ru")}</Option>
                        </Select>
                    </Form.Item>

                    {!lead.degree && (
                        <Form.Item
                            name="degree"
                            label={t("application.degreeUpdate")}
                            rules={[{ required: true, message: t("lead.selectDegree") }]}>
                            <Select
                                placeholder={t("lead.selectDegree")}
                                loading={degreeLoading}
                                showSearch
                            >
                                {degrees?.map((item) => (
                                    <Option key={item.id} value={item.name}>
                                        {item.name}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    )}

                    <Form.Item>
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={isAdding}
                            block>
                            {t("application.submitApplication")}
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title={canUpdate ? t("application.updateApplication") : (t("common.view") + " " + t("application.title"))}
                open={isStatusModalOpen}
                onCancel={() => setIsStatusModalOpen(false)}
                footer={null}
                width={1400}
            >
                <Form form={statusForm} disabled={!canUpdate} layout="vertical" onFinish={onStatusFinish}>
                    <Form.Item
                        name="status"
                        label={t("application.status")}
                        rules={[{ required: true }]}>
                        <Select placeholder={t("application.selectStatus")}>
                            {statusOptions.map(opt => (
                                <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="replay"
                        label={t("application.universityReply")}>
                        <Input.TextArea
                            rows={2}
                            placeholder={t("application.enterUniversityReply")}
                        />
                    </Form.Item>

                    <Form.Item
                        name="university_code"
                        label={t("application.universityCode")}>
                        <Input placeholder={t("application.enterUniversityCode")} />
                    </Form.Item>

                    <Form.Item
                        name="amount"
                        label={t("application.amount")}>
                        <Input
                            type="number"
                            step="0.01"
                            placeholder={t("application.enterAmount")}
                            prefix="$"
                        />
                    </Form.Item>

                    <Form.Item
                        label={t("application.receipt")}>
                        <Upload
                            maxCount={1}
                            beforeUpload={(file) => {
                                setReceiptFile(file);
                                return false; // Prevent auto upload
                            }}
                            onRemove={() => setReceiptFile(null)}
                        >
                            <Button icon={<UploadOutlined />}>{t("application.selectReceiptFile")}</Button>
                        </Upload>
                        {statusForm.getFieldValue('receipt') && (
                            <div className="mt-2 flex gap-2">
                                <Button
                                    type="link"
                                    icon={<EyeOutlined />}
                                    onClick={() => window.open(getAttachmentUrl(statusForm.getFieldValue('receipt')), "_blank")}
                                >
                                    {t("application.view")}
                                </Button>
                                <Button
                                    type="link"
                                    icon={<DownloadOutlined />}
                                    onClick={() => {
                                        const url = getAttachmentUrl(statusForm.getFieldValue('receipt'));
                                        const link = document.createElement("a");
                                        link.href = url;
                                        link.target = "_blank";
                                        link.download = "receipt";
                                        document.body.appendChild(link);
                                        link.click();
                                        document.body.removeChild(link);
                                    }}
                                >
                                    {t("attachment.download")}
                                </Button>
                            </div>
                        )}
                        <div className="text-xs text-gray-500 mt-1">{t("application.uploadReceiptInfo")}</div>
                    </Form.Item>

                    <Form.Item
                        name="payment_status"
                        label={t("application.paymentStatus")}>
                        <Select placeholder={t("application.selectPaymentStatus")}>
                            {paymentStatusOptions.map(opt => (
                                <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="note"
                        label={t("application.note")}>
                        <Mentions
                            rows={4}
                            placeholder={t("application.enterNotes")}
                            options={staffList?.map(staff => ({
                                value: staff.username || `${staff.firstName}.${staff.lastName}`,
                                label: `${staff.firstName} ${staff.lastName}`,
                            }))}
                        />
                    </Form.Item>

                    {canUpdate && (
                        <Form.Item>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={isUpdating || isProcessing}
                                block>
                                {t("application.updateApplication")}
                            </Button>
                        </Form.Item>
                    )}
                </Form>
            </Modal>
        </div>
    );
}
